import {
    IonBackButton, IonButton,
    IonButtons,
    IonContent,
    IonGrid,
    IonHeader,
    IonPage,
    IonRow,
    IonTitle,
    IonToolbar, IonItem,
    IonCol, IonLabel, IonChip, IonIcon, IonProgressBar
} from '@ionic/react';
import {isPlatform, useIonAlert} from '@ionic/react';
import React, {memo} from "react";
import styles from './AccessAlarm.module.css';
import {
    shieldCheckmark,
    shieldHalf,
    lockOpenOutline,
    printOutline,
    textOutline,
    warning
} from "ionicons/icons";
import useUX from "../ux/UX";
import {
    useAlarmDisplayQuery,
    useAlarmStateQuery,
    useSendAlarmKeyMutation,
    useSetAlarmMutation
} from "../query/buildingStateQuery";
import {useGatewayConnectionStateQuery} from "../query/gatewayConnectionStateQuery";
import {encodeAlarmTextToKeySequence, maximumAlarmTextLength} from "./alarmTextEncoder";

const displayCharacterColumns = Array.from({length: 16}, (_, index) => index);
const displayRows = [0, 1];
const displayPlaceholderCharacter = '\u0953';

const AccessAlarm: React.FC = () => {
    const {data: alarmState = ''} = useAlarmStateQuery();
    const {data: alarmDisplay = {line1: '-', line2: '-'}} = useAlarmDisplayQuery();
    const {line1, line2} = alarmDisplay;
    const {isConnecting} = useGatewayConnectionStateQuery();
    const setAlarmMutation = useSetAlarmMutation();
    const sendAlarmKeyMutation = useSendAlarmKeyMutation();
    const {presentError, presentResult} = useUX();
    const [presentAlert] = useIonAlert();

    async function setAlarm(set: boolean) {
        await presentResult(async () => {
            await setAlarmMutation.mutateAsync(set);
        }, `${set ? 'Set' : 'Unset'} Command Issued`);
    }

    async function sendKey(key: string) {
        try {
            await sendAlarmKeyMutation.mutateAsync(key);
        } catch (error: unknown) {
            await presentError(error);
        }
    }

    async function sendText(text: string) {
        await presentResult(async () => {
            const keySequence = encodeAlarmTextToKeySequence(text);
            await sendAlarmKeyMutation.mutateAsync(keySequence);
        }, 'Text Sent to Alarm');
    }

    async function promptForText() {
        await presentAlert({
            header: 'Enter Alarm Text',
            message: `Enter up to ${maximumAlarmTextLength} characters. Lower-case letters will be sent as upper case.`,
            inputs: [{
                name: 'text',
                type: 'text',
                placeholder: 'Text to enter',
                attributes: {
                    maxlength: maximumAlarmTextLength,
                    autocapitalize: 'characters',
                    spellcheck: false
                }
            }],
            buttons: [
                'Cancel',
                {
                    text: 'Send',
                    handler: ({text}) => {
                        void sendText(text ?? '');
                    }
                }
            ]
        });
    }

    return (
        <IonPage>
            <IonHeader translucent={isPlatform('capacitor')}>
                <IonToolbar>
                    <IonButtons slot="start">
                        <IonBackButton defaultHref="../"/>
                    </IonButtons>
                    <IonTitle>Alarm Control</IonTitle>
                    <IonButtons slot="end">
                        <IonButton routerLink="/access/alarm/printer-log" aria-label="Alarm printer log">
                            <IonIcon slot="icon-only" icon={printOutline}/>
                        </IonButton>
                        <IonButton disabled={sendAlarmKeyMutation.isPending} onClick={promptForText}
                                   aria-label="Enter alarm text">
                            <IonIcon slot="icon-only" icon={textOutline}/>
                        </IonButton>
                    </IonButtons>
                    {!!(isConnecting || setAlarmMutation.isPending || sendAlarmKeyMutation.isPending)
                        && <IonProgressBar type="indeterminate"/>}
                </IonToolbar>
            </IonHeader>
            <IonContent fullscreen={true}>
                <IonHeader collapse="condense">
                    <IonToolbar>
                        <IonTitle size="large">Alarm Control</IonTitle>
                    </IonToolbar>
                </IonHeader>
                <div className={styles.alarmDisplay}>
                    <div className={styles.alarmDisplayBackground} aria-hidden="true">
                        {displayRows.map(row => (
                            <div key={row} className={styles.alarmDisplayBackgroundLine}>
                                {displayCharacterColumns.map(column => (
                                    <span key={column} className={styles.alarmDisplayBackgroundCell}>
                                        {displayPlaceholderCharacter}
                                    </span>
                                ))}
                            </div>
                        ))}
                    </div>
                    <div className={styles.alarmDisplayContent}>
                        <div className={styles.alarmDisplayLine}>{line1}</div>
                        <div className={styles.alarmDisplayLine}>{line2}</div>
                    </div>
                </div>
                <IonItem lines="full">
                    <IonChip slot="start" color={{
                        ARMED: 'danger',
                        TRIGGERED: 'danger',
                        DISARMED: 'success'
                    }[alarmState] || 'warning'}>
                        <IonIcon
                            icon={{
                                ARMED: shieldCheckmark,
                                DISARMED: lockOpenOutline,
                                TRIGGERED: warning
                            }[alarmState] || shieldHalf}/>
                        <IonLabel className={styles.alarmStateLabel}>{{
                            ARMED: 'Armed',
                            DISARMED: 'Disarmed',
                            TRIGGERED: 'TRIGGERED'
                        }[alarmState] || alarmState}</IonLabel>
                    </IonChip>
                    <IonButton slot="end" disabled={!line1.includes('Galaxy 48') || setAlarmMutation.isPending}
                               onClick={() => setAlarm(alarmState === 'DISARMED')} className={styles.button}>
                        <IonIcon slot="start"
                                 icon={alarmState === 'DISARMED' ? shieldCheckmark : lockOpenOutline}/>
                        {alarmState === 'DISARMED' ? 'Set Alarm' : 'Unset Alarm'}
                    </IonButton>
                </IonItem>
                <IonGrid className={styles.keypad}>
                    <IonRow>
                        <IonCol><IonButton expand="block" onClick={() => sendKey('1')}>1</IonButton></IonCol>
                        <IonCol><IonButton expand="block" onClick={() => sendKey('2')}>2</IonButton></IonCol>
                        <IonCol><IonButton expand="block" onClick={() => sendKey('3')}>3</IonButton></IonCol>
                        <IonCol><IonButton expand="block" onClick={() => sendKey('A')}>A ▶</IonButton></IonCol>
                    </IonRow>
                    <IonRow>
                        <IonCol><IonButton expand="block" onClick={() => sendKey('4')}>4</IonButton></IonCol>
                        <IonCol><IonButton expand="block" onClick={() => sendKey('5')}>5</IonButton></IonCol>
                        <IonCol><IonButton expand="block" onClick={() => sendKey('6')}>6</IonButton></IonCol>
                        <IonCol><IonButton expand="block" onClick={() => sendKey('B')}>B ◀</IonButton></IonCol>
                    </IonRow>
                    <IonRow>
                        <IonCol><IonButton expand="block" onClick={() => sendKey('7')}>7</IonButton></IonCol>
                        <IonCol><IonButton expand="block" onClick={() => sendKey('8')}>8</IonButton></IonCol>
                        <IonCol><IonButton expand="block" onClick={() => sendKey('9')}>9</IonButton></IonCol>
                        <IonCol><IonButton color="success" expand="block"
                                           onClick={() => sendKey('E')}>ent</IonButton></IonCol>
                    </IonRow>
                    <IonRow>
                        <IonCol><IonButton expand="block" onClick={() => sendKey('*')}>*</IonButton></IonCol>
                        <IonCol><IonButton expand="block" onClick={() => sendKey('0')}>0</IonButton></IonCol>
                        <IonCol><IonButton expand="block" onClick={() => sendKey('#')}>#</IonButton></IonCol>
                        <IonCol><IonButton color="danger" expand="block"
                                           onClick={() => sendKey('X')}>esc</IonButton></IonCol>
                    </IonRow>
                </IonGrid>
            </IonContent>
        </IonPage>
    );
};

export default memo(AccessAlarm);
